import { FieldIds } from '@/constants/FieldIds';
import { FieldNames } from '@/constants/FieldNames';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IComp from '@/models/Comp';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IFormFieldRule from '@/models/form-field/IFormFieldRule';
import IFormFieldRules from '@/models/form-field/IFormFieldRules';
import IFormFields from '@/models/forms/IFormFields';
import FormsService from '@/services/3.6/FormsService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import { Getters, originalValuesStore, store } from '@/store';
import OriginalValuesManager from './OriginalValuesManager';

const repeatableFieldRulesCache = new Map<string, Promise<IFormFieldRules | null>>();

/**
 * Manages field rules using the FormsService (QS API)
 */
export default {
    /**
     * Build an array of form field rules from the given comp's source data.
     * Ignores form filler values, comp photos and comp data w/o a field id.
     * Will use any existing original value in place of transfer value.
     * @param comp
     * @returns an array of form field rules
     */
    buildFromComp(comp: IComp, excludeNoValueAndNoSourceInitial?: boolean): IFormFieldRules {
        const fieldRules: IFormFieldRules = [];

        const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);

        if (comp.sourceData) {
            const originalValuesProperty = originalValuesStore.OriginalValuesByAddress1AndUnitNo(address1AndUnitNo);
            const compDatasByFieldId = new Map<number, typeof comp.sourceData[number]>();
            for (const compData of comp.sourceData) {
                if (
                    compData.sourceInitial === SourceDataInitials.FormFiller ||
                    compData.fieldName === FieldNames.CompPhoto ||
                    compData.fieldId === undefined ||
                    compData.fieldId === null
                ) {
                    continue;
                }

                const current = compDatasByFieldId.get(compData.fieldId);
                if (shouldUseCompDataCandidate(current, compData)) {
                    compDatasByFieldId.set(compData.fieldId, compData);
                }
            }

            const compDatas = Array.from(compDatasByFieldId.values());
            for (const compData of compDatas) {
                if (compData.fieldId !== undefined && compData.fieldId !== null) {
                    if (typeof compData.value === 'string') {
                        if (excludeNoValueAndNoSourceInitial && !compData.value && !compData.sourceInitial) {
                            continue;
                        }

                        // Use any existing original value
                        const originalValue = originalValuesProperty?.find((ov) => ov.fieldId === compData.fieldId);

                        const fieldRule: IFormFieldRule = {
                            fieldId: compData.fieldId,
                            value: getRuleInputValue(compData.value, originalValue?.originalValue),
                        };
                        fieldRules.push(fieldRule);

                    } else {
                        console.log(`FieldRulesManager.buildFromComp(), Comp data value isn't a string[${compData.value}] on field[${compData.fieldName}]`);
                    }
                } else {
                    console.log(`FieldRulesManager.buildFromComp(), Comp data has no fieldId on field[${compData.fieldName}]`);
                }
            }
        } else {
            console.log(`FieldRulesManager.buildFromComp(), Comp[${address1AndUnitNo}] has no source data.`);
        }

        return fieldRules;
    },

    buildFromCompForFields(comp: IComp, formFields: IFormFields): IFormFieldRules {
        const fieldRules: IFormFieldRules = [];

        if (comp.sourceData) {
            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);
            const originalValuesProperty = OriginalValuesManager.getPropertyByAddress(comp.address);
            //console.log(`FieldRulesManager.buildFromCompForFields(), Address1AndUnitNo[${address1AndUnitNo}], Original values[${originalValuesProperty ? originalValuesProperty.length : 0}]`);

            for (const fieldData of formFields) {
                const compData = comp.sourceData.find((cd) => cd.fieldName === fieldData.fieldName);
                if (compData && compData.sourceInitial !== SourceDataInitials.FormFiller && compData.fieldName !== FieldNames.CompPhoto) {
                    // Use any existing original value
                    const originalValue = originalValuesProperty?.find((ov) => ov.fieldId === fieldData.fieldId);

                    const fieldRule: IFormFieldRule = {
                        fieldId: fieldData.fieldId,
                        value: originalValue ? originalValue.originalValue : compData.value,
                    };
                    fieldRules.push(fieldRule);
                    //console.log(`FieldRulesManager.buildFromCompForFields(), Added field rule[${JSON.stringify(fieldRule)}], original value[${originalValue ? originalValue.originalValue : ''}]`);
                }
            }
        }

        //console.log(`FieldRulesManager.buildFromCompForFields(), Field rules[${JSON.stringify(fieldRules)}]`);
        return fieldRules;
    },

    /**
     * Build a form field rules object from the given comp's source data for the given fieldId.
     * Ignores form filler values and comp photos.
     * Note: The comp data must have a fieldId value (not null or undefined).
     * Will use any existing original value in place of transfer value.
     * @param comp
     * @param fieldId
     * @returns an array of form field rules having one or zero items based on the comp and fieldId.
     */
    buildFromCompForField(comp: IComp, fieldId: number): IFormFieldRules {
        const fieldRules: IFormFieldRules = [];

        if (comp.sourceData) {
            const requestedFieldIdIsRepeatable = fieldId % 1 !== 0;
            const matchingCompDatasByFieldId = new Map<number, typeof comp.sourceData[number]>();
            for (const compData of comp.sourceData) {
                const isMatchingFieldId = requestedFieldIdIsRepeatable
                    ? compData.fieldId === fieldId
                    : typeof compData.fieldId === 'number' && Math.floor(compData.fieldId) === fieldId;
                if (
                    !isMatchingFieldId ||
                    compData.sourceInitial === SourceDataInitials.FormFiller ||
                    compData.fieldId === FieldIds.CompPhoto
                ) {
                    continue;
                }

                const current = matchingCompDatasByFieldId.get(compData.fieldId);
                if (shouldUseCompDataCandidate(current, compData)) {
                    matchingCompDatasByFieldId.set(compData.fieldId, compData);
                }
            }

            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);
            const originalValuesProperty = originalValuesStore.OriginalValuesByAddress1AndUnitNo(address1AndUnitNo);
            for (const compData of matchingCompDatasByFieldId.values()) {
                // Use any existing original value
                const originalValue = originalValuesProperty?.find((ov) => ov.fieldId === compData.fieldId);

                const fieldRule: IFormFieldRule = {
                    fieldId: compData.fieldId,
                    value: getRuleInputValue(compData.value, originalValue?.originalValue),
                };
                fieldRules.push(fieldRule);
            }
        }
        return fieldRules;
    },

    /**
     * Run field rules using the FormsService (QS API) on the given field rules.
     * The resulting form field rules are associated with the comp identified by the given comp address.
     * The comp address is necessary to maintain the original values store state.
     * @param fieldRules
     * @returns
     */
    async runFieldRulesForComp(compAddress: ICompAddress, fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
        // For better code reuse, we can call the processRunFieldRules method for both runFieldRule and runFieldRulesForComp
        const fieldRulesToRun = this.processRunFieldRules(compAddress, fieldRules);
        return fieldRulesToRun;
    },

    /**
     * Run field rules using the FormsService (QS API) on the given field rules.
     * The resulting form field rules are associated with the active comp.
     * The comp address is necessary to maintain the original values store state.
     * @param fieldRules
     * @returns
     */
    async runFieldRules(fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
        const activeComp: IComp = store.getters[Getters.CompAddressesV36.ActiveComp];
        const formFieldRules = await this.runFieldRulesForComp(activeComp.address, fieldRules);
        return formFieldRules;
    },

    /**
     * Run field rules using the FormsService (QS API) on the given field rule.
     * The resulting form field rules are associated with the active comp.
     * The comp address is necessary to maintain the original values store state.
     * @param fieldRule
     * @returns
     */
    async runFieldRule(fieldRule: IFormFieldRule): Promise<IFormFieldRules | null> {
        // For better code reuse, we can call the processRunFieldRules method for both runFieldRule and runFieldRulesForComp
        const activeComp: IComp = store.getters[Getters.CompAddressesV36.ActiveComp];
        const fieldRulesToRun = this.processRunFieldRules(activeComp.address, [fieldRule]);
        return fieldRulesToRun;
    },

    async processRunFieldRules(compAddress: ICompAddress, fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
        if (fieldRules.length > 0) {
            const fieldRuleFamilyCounts = fieldRules.reduce((counts, rule) => {
                const parentFieldId = Math.floor(rule.fieldId);
                counts.set(parentFieldId, (counts.get(parentFieldId) ?? 0) + 1);
                return counts;
            }, new Map<number, number>());
            const repeatableFieldRules = fieldRules.filter(
                (rule) => rule.fieldId % 1 !== 0 || (fieldRuleFamilyCounts.get(Math.floor(rule.fieldId)) ?? 0) > 1
            );
            const nonRepeatableFieldRules = fieldRules.filter(
                (rule) => rule.fieldId % 1 === 0 && (fieldRuleFamilyCounts.get(Math.floor(rule.fieldId)) ?? 0) === 1
            );

            let fieldRulesToRun: IFormFieldRules = [];

            if (nonRepeatableFieldRules.length > 0) {
                const nonRepeatableFormFieldRules = await runFieldRulesService(nonRepeatableFieldRules);
                if (nonRepeatableFormFieldRules) {
                    // Maintain the original values store state
                    await OriginalValuesManager.updateFromFieldRules(compAddress, nonRepeatableFieldRules, nonRepeatableFormFieldRules);
                    fieldRulesToRun.push(...nonRepeatableFormFieldRules);
                }
            }

            const repeatableRuleBatches: IFormFieldRules[] = [];
            for (const repeatableFieldRule of repeatableFieldRules) {
                const parentFieldId = Math.floor(repeatableFieldRule.fieldId);
                let batch = repeatableRuleBatches.find((candidate) =>
                    !candidate.some((rule) => Math.floor(rule.fieldId) === parentFieldId)
                );

                if (!batch) {
                    batch = [];
                    repeatableRuleBatches.push(batch);
                }

                batch.push(repeatableFieldRule);
            }

            for (const repeatableFieldRuleBatch of repeatableRuleBatches) {
                const serviceResults = await runCachedRepeatableFieldRulesService(
                    repeatableFieldRuleBatch.map((repeatableFieldRule) => ({
                        ...repeatableFieldRule,
                        fieldId: Math.floor(repeatableFieldRule.fieldId),
                    }))
                );
                if (!serviceResults) {
                    continue;
                }

                const exactResults = repeatableFieldRuleBatch
                    .map((repeatableFieldRule, index) => {
                        const serviceResult = serviceResults[index];
                        return serviceResult
                            ? { ...serviceResult, fieldId: repeatableFieldRule.fieldId }
                            : null;
                    })
                    .filter((result): result is IFormFieldRules[number] => result !== null);

                if (exactResults.length > 0) {
                    await OriginalValuesManager.updateFromFieldRules(
                        compAddress,
                        repeatableFieldRuleBatch.slice(0, exactResults.length),
                        exactResults
                    );
                    fieldRulesToRun.push(...exactResults);
                }
            }

            return fieldRulesToRun.length > 0 ? fieldRulesToRun : null;
        }
        return null;
    },
};

async function runFieldRulesService(fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    const service = new FormsService(webEnvConfig);
    const formFieldRules = await service.runFieldRules(fieldRules);
    return formFieldRules;
}

async function runCachedRepeatableFieldRulesService(fieldRules: IFormFieldRules): Promise<IFormFieldRules | null> {
    const cacheKey = JSON.stringify(fieldRules);
    let cachedResult = repeatableFieldRulesCache.get(cacheKey);

    if (!cachedResult) {
        cachedResult = runFieldRulesService(fieldRules);
        repeatableFieldRulesCache.set(cacheKey, cachedResult);
    }

    const result = await cachedResult;
    return result?.map((fieldRule) => ({ ...fieldRule })) ?? null;
}

function getRuleInputValue(currentValue: string, originalValue?: string): string {
    if (originalValue === undefined || (originalValue.trim() === '' && currentValue.trim() !== '')) {
        return currentValue;
    }
    return originalValue;
}

function shouldUseCompDataCandidate(current: any, candidate: any): boolean {
    if (!current) {
        return true;
    }

    // The first repeat has the integer parent ID (for example 1119), so it can
    // share an ID with a legacy base-field transfer record. The grid renders the
    // flattened indexed record; use that same record as the rule input.
    const currentIsFlattenedRepeat = /-\d+(?:-|$)/.test(String(current.fieldName || ''));
    const candidateIsFlattenedRepeat = /-\d+(?:-|$)/.test(String(candidate.fieldName || ''));
    if (currentIsFlattenedRepeat !== candidateIsFlattenedRepeat) {
        return candidateIsFlattenedRepeat;
    }

    const currentHasValue = typeof current.value === 'string' && current.value.trim() !== '';
    const candidateHasValue = typeof candidate.value === 'string' && candidate.value.trim() !== '';
    return !currentHasValue && candidateHasValue;
}
